import React, { useEffect, useState, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ReqStatus } from 'src/api/types';
import { updateToken, validateSms } from 'src/api/api';
import { unavailableAccountsMessage, updateAccountTokens } from 'src/api/accountTokens';
import {
    initConnectSuccess,
    setErrorMessage,
    setNeedsPrivacyPolicy,
    setPrivacyPolicy,
    setUserMobile,
} from 'src/store/actions/loginAction';
import { ILoginState, PrivacyPolicy } from 'src/store/type';
import { IAccount, ResPinCodeSignup } from 'src/types/login';
import Alert from 'src/components/commons/Alert/Alert';
import ActionConfirmModal from 'src/components/Modals/ActionConfirmModal';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utilis/utilis';
import { StorageKeys } from 'src/types/global';
import { images } from 'src/assets';

import styles from './InitialIdentification.style';
import Phone from './Phone/Phone';
import Code from './Code/Code';

interface Props { }

const InitialIdentification = (props: Props): JSX.Element => {
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const onSetLoginData = (payload: ILoginState) => dispatch(initConnectSuccess(payload));
    const [errorAlert, setErrorAlert] = useState({
        isShow: false,
        text: '',
    });
    const [keyCode, setKeyCode] = useState<string>(uuidv4());
    const [isExpiredPinCode, setIsExpiredPinCode] = useState<boolean>(false);
    const [phone, setPhone] = useState('');
    const [isTypeCodeMode, setisTypeCodeMode] = useState(false);
    const [showPhoneNotFoundModal, setShowPhoneNotFoundModal] = useState(false);
    const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
    const setErrorAlertRef = useRef(setErrorAlert);
    setErrorAlertRef.current = setErrorAlert;

    const TOAST_DURATION_S = 6;

    useEffect(() => {
        if (!lockedUntil) return;
        let ticks = 0;
        const interval = setInterval(() => {
            ticks++;
            const remaining = lockedUntil.getTime() - Date.now();
            if (remaining <= 0 || ticks >= TOAST_DURATION_S) {
                clearInterval(interval);
                setLockedUntil(null);
                return;
            }
            const minutes = Math.floor(remaining / 60000);
            const seconds = Math.floor((remaining % 60000) / 1000);
            const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
            setErrorAlertRef.current({ isShow: true, text: `${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}` });
        }, 1000);
        return () => clearInterval(interval);
    }, [lockedUntil, t]);
    const decodePhone = useMemo(
        () => (phone.length === 10 ? phone.replace(phone.substring(3, 7), '****') : ''),
        [phone]
    );
    useEffect(() => {
        if (!getLocalStorageValue(StorageKeys.DeviceToken)) {
            const newDeviceToken = uuidv4();
            setLocalStorageValue(StorageKeys.DeviceToken, newDeviceToken);
        }
    }, []);

    useEffect(() => {
        if (isExpiredPinCode)
            setTimeout(() => {
                history.push('/');
            }, 3000);
    }, [isExpiredPinCode, history]);

    const onSendSmsSuccess = (phoneNumber: string, newDeviceToken: string) => {
        setLocalStorageValue(StorageKeys.DeviceToken, newDeviceToken);
        setPhone(phoneNumber);
        setisTypeCodeMode(true);
    };

    const onValidateSms = async (code: string) => {
        try {
            const deviceToken = getLocalStorageValue(StorageKeys.DeviceToken);
            const res = await validateSms({
                verifyCode: code,
                deviceToken,
            });

            if (res.data.response === ResPinCodeSignup.SUCCESS) {
                const { data } = res.data;
                setLocalStorageValue(StorageKeys.DeviceToken, res.data.$deviceToken);
                const loginData: ILoginState = data[0] || {};
                const fcAccounts: IAccount[] = loginData.fcAccounts || [];
                const token: string = loginData.token || '';
                const indexerR: string = loginData.indexerR || '';

                const { available, unavailable } = await updateAccountTokens(
                    fcAccounts,
                    ({ proxyUrl, contactUUID, dbUrl, proxyToken }) =>
                        updateToken({
                            token,
                            proxyUrl,
                            dbUrl,
                            contactUUID,
                            indexerR,
                            accessToken: proxyToken?.accessToken || '',
                            tokenType: proxyToken?.tokenType || 'Bearer',
                        })
                );

                if (fcAccounts.length && !available.length) {
                    setKeyCode(uuidv4());
                    setErrorAlert({ isShow: true, text: t('allCompaniesUnavailable') });
                    return;
                }
                if (unavailable.length) dispatch(setErrorMessage(unavailableAccountsMessage(unavailable, t)));

                onSetLoginData({
                    isAuthenticated: ReqStatus.SUCCESS,
                    ...data[0],
                    fcAccounts: available,
                });
                history.push('signup-options');
            } else if (res.data.response === ResPinCodeSignup.PRIVACY_POLICY_NOT_APPROVED) {
                const { data } = res.data;

                const privacyPolicy: PrivacyPolicy = {
                    versionNumber: data.versionNumber,
                    policyContent: data.policyContent,
                    policyUrl: data.policyUrl,
                    policyDocTypeId: data.policyDocTypeId,
                };

                // history.push('signup-options');
                dispatch(setUserMobile(phone));
                dispatch(setPrivacyPolicy(privacyPolicy));
                dispatch(setNeedsPrivacyPolicy(true));

            } else if (res.data.response === ResPinCodeSignup.WRONG_PIN_CODE) {
                setKeyCode(uuidv4());
                setErrorAlert({ isShow: true, text: t('wrongCode') });
            } else if (res.data.response === ResPinCodeSignup.WRONG_PIN_CODE_TO_MANY_TIMES) {
                setKeyCode(uuidv4());
                setErrorAlert({ isShow: true, text: t('wrongCodeToManyTimes') });
            } else if (res.data.response === ResPinCodeSignup.EXPIRED_TOKEN) {
                setKeyCode(uuidv4());
                setIsExpiredPinCode(true);
                setErrorAlert({ isShow: true, text: t('expiredPinCode') });
            } else if (res.data.response === ResPinCodeSignup.FAILED_TO_FIND_PHONE) {
                setKeyCode(uuidv4());
                setShowPhoneNotFoundModal(true);
            } else if (res.data.response === ResPinCodeSignup.TOO_MANY_ATTEMPTS_LOCKED) {
                const lockedUntilUTC = res.data.data?.lockedUntilUTC;
                if (lockedUntilUTC) {
                    const lockDate = new Date(lockedUntilUTC);
                    const remaining = lockDate.getTime() - Date.now();
                    const minutes = Math.floor(remaining / 60000);
                    const seconds = Math.floor((remaining % 60000) / 1000);
                    const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                    setErrorAlert({ isShow: true, text: `${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}` });
                    setLockedUntil(lockDate);
                } else {
                    setErrorAlert({ isShow: true, text: t('wrongCodeToManyTimes') });
                }
            }
             else {
                setKeyCode(uuidv4());
                setErrorAlert({ isShow: true, text: t('wrongCode') });
            }
        } catch (error) {
            setKeyCode(uuidv4());
            setErrorAlert({ isShow: true, text: t('wrongCode') });
            console.log('e', error);
        }
    };

    const handleCloseSnack = () => setErrorAlert((preState) => ({ ...preState, isShow: false }));

    const TitleComponent = (
        <>
            <styles.Text>{t('sendSmsForPhone')}</styles.Text>
            <styles.PhoneText>{decodePhone}</styles.PhoneText>
        </>
    );

    const onPhoneError = (errorMassage: string) => {
        setErrorAlert({ isShow: true, text: errorMassage });
    };

    const isAutoSubmit = true;

    return (
        <styles.SpaceAround>
            {isTypeCodeMode ? (
                <Code
                    key={keyCode}
                    isRefresh={errorAlert.isShow}
                    isAutoSubmit={isAutoSubmit}
                    Title={TitleComponent}
                    onSubmit={onValidateSms}
                    btnText={t('connect')}
                />
            ) : (
                <Phone onSubmit={onSendSmsSuccess} onError={onPhoneError} />
            )}
            <Alert open={errorAlert.isShow} onClose={handleCloseSnack} severity="error">
                {errorAlert.text}
            </Alert>
            <ActionConfirmModal
                isOpen={showPhoneNotFoundModal}
                isRedirectPopup
                onConfirm={() => {
                    setShowPhoneNotFoundModal(false);
                    setisTypeCodeMode(false);
                }}
                Img={images.alartIcon}
                Body={() => <span>{t('phoneNumberWasntFound')}</span>}
            />
        </styles.SpaceAround>
    );
};

export default InitialIdentification;
